"use client";

import { ArrowRight, MapPin, Plus } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function FindUsInTheNeighborhood() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const infoRows = [{
    label: "OFFICE",
    value: "42 Community Way, Suite 100, Hope City"
  }, {
    label: "CENTER",
    value: "Main Building, Ground Floor"
  }, {
    label: "PARKING",
    value: "Accessible spots available at front"
  }, {
    label: "TRANSIT",
    value: "Central Station (Bus Lines 4, 8)"
  }, {
    label: "ACCESS",
    value: "Fully ADA compliant facility"
  }];
  return <section data-section-id="3054" id="find-us-in-the-neighborhood" className="bg-background text-foreground py-20 md:py-32 px-4 sm:px-6 lg:px-12">
      <div ref={ref} className={`max-w-7xl mx-auto transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
        {}
        <div className="border-t border-border pt-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 pb-4">
            <span className="font-default text-xs tracking-widest text-primary">
              {"// OUR COMMUNITY HUB"}
            </span>
            <span className="font-default text-xs tracking-widest text-muted-foreground">
              LAT 34.0522 / LONG 118.2437
            </span>
          </div>
          <div className="border-b border-border" />
        </div>

        {}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 mt-12 lg:mt-16">
          {}
          <div className="lg:col-span-5">
            <h2 className="font-default font-bold text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tight">
              Visit our{" "}
              <span className="font-serif italic font-normal">neighborhood.</span>
            </h2>

            <p className="font-default text-lg leading-relaxed text-muted-foreground mt-6 max-w-md">
              Hope Torchlighters is located in the heart of the city, providing a 
              welcoming space for families to connect, learn, and grow. Our center 
              is designed to be a safe, inclusive environment for all neurodiverse 
              individuals and their caregivers.
            </p>

            {}
            <div className="mt-10 border-t border-border">
              {infoRows.map((row, i) => <div key={i} data-index={i} className="grid grid-cols-1 sm:grid-cols-[160px_1fr] gap-1 sm:gap-6 py-4 border-b border-border">
                  <span className="font-default text-xs tracking-widest text-muted-foreground pt-1">
                    {row.label} //
                  </span>
                  <span className="font-default text-base text-foreground leading-snug">
                    {row.value}
                  </span>
                </div>)}
            </div>

            {}
            <div className="mt-8">
              <Button asChild variant="link" className="text-secondary hover:text-secondary/80 font-default text-sm tracking-widest px-0 h-auto">
                <Link to="/ConnectPage">
                  GET DIRECTIONS <ArrowRight className="ml-1 h-4 w-4" />
                </Link>
              </Button>
            </div>
          </div>

          {}
          <div className="lg:col-span-7">
            <div className="relative bg-card/40 backdrop-blur-md border border-border shadow-lg rounded-lg overflow-hidden">
              {}
              <div className="relative aspect-[4/3] lg:aspect-[16/12] w-full overflow-hidden">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700933/img/community_center_map.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700933/img/community_center_map.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700933/img/community_center_map.png-wvc-sizes" alt="Hope Torchlighters community center location" loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover" />

                {}
                <div className="absolute inset-0 opacity-30 pointer-events-none" style={{
                backgroundImage: "linear-gradient(to right, hsla(241, 43%, 43%, 0.1) 1px, transparent 1px), linear-gradient(to bottom, hsla(241, 43%, 43%, 0.1) 1px, transparent 1px)",
                backgroundSize: "40px 40px"
              }} />

                {}
                <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
                  <div className="relative flex items-center justify-center">
                    <span className="absolute w-16 h-16 border border-secondary/40 animate-ping rounded-lg" />
                    <span className="absolute w-10 h-10 border border-secondary/70 rounded-lg" />
                    <span className="relative w-4 h-4 bg-secondary shadow-md rounded-sm" />
                  </div>
                  <div className="absolute top-full left-1/2 -translate-x-1/2 mt-3 whitespace-nowrap">
                    <span className="font-default text-[10px] tracking-widest text-secondary bg-background/80 backdrop-blur-sm px-2 py-1 border border-secondary/40 rounded-md">
                      HOPE // CENTER
                    </span>
                  </div>
                </div>

                {}
                <div className="absolute top-4 left-4 bg-background/70 backdrop-blur-md border border-border px-3 py-2 shadow-md rounded-md">
                  <div className="flex items-center gap-2">
                    <MapPin className="h-3 w-3 text-secondary" />
                    <span className="font-default text-[10px] tracking-widest text-muted-foreground">
                      34°03&apos;08.0&quot;N / 118°14&apos;36.0&quot;W
                    </span>
                  </div>
                  <div className="mt-1 font-default text-[10px] tracking-widest text-foreground">
                    ZONE 11S / ALT 85M
                  </div>
                </div>

                {}
                <div className="absolute top-4 right-4 bg-background/70 backdrop-blur-md border border-border px-3 py-2 shadow-md rounded-md">
                  <span className="font-default text-[10px] tracking-widest text-muted-foreground">
                    SCALE 1:5000
                  </span>
                </div>
              </div>

              {}
              <div className="border-t border-border bg-background/60 backdrop-blur-md px-4 py-3">
                <span className="font-default text-[10px] sm:text-xs tracking-widest text-muted-foreground">
                  OPEN MON–FRI 09:00–17:00 / SAT BY APPOINTMENT / CLOSED SUN
                </span>
              </div>
            </div>
          </div>
        </div>

        {}
        <div className="mt-16 lg:mt-20 relative">
          <div className="border-t border-border" />
          <div className="absolute -top-2 left-0">
            <Plus className="h-4 w-4 text-primary" strokeWidth={1} />
          </div>
          <div className="absolute -top-2 right-0">
            <Plus className="h-4 w-4 text-primary" strokeWidth={1} />
          </div>
          <div className="absolute -top-2 left-1/2 -translate-x-1/2">
            <Plus className="h-4 w-4 text-primary" strokeWidth={1} />
          </div>
        </div>
      </div>
    </section>;
}